import { useTranslation } from 'react-i18next'

import { getCountryNameFromAlpha3Code } from '../../../../../i18n/countries'
import { OrganisationVerificationStatus } from '../../../../../services/grants/grantsService.types'
import styles from '../CandidateDetailsContainer.module.scss'

import type { BadgeVariant, IconRegistry } from '@percent/lemonade'
import { Badge, CardContent, CardDashboard, FlagAvatar, Flex } from '@percent/lemonade'
import { useCountries } from '@percent/partner-dashboard/common/hooks/useCountries/useCountries'
import type { CandidateCardProps } from './CandidateCard.types'

type CandidateBadgeProps = {
  variant: BadgeVariant
  icon?: keyof typeof IconRegistry
  text: string
}

export function CandidateCard({ candidate }: Readonly<CandidateCardProps>) {
  const { t } = useTranslation()
  const { getCountryNameByCode } = useCountries()
  const { organisation } = candidate
  const { verificationStatus } = organisation

  const candidateStateToBadgeMapping = (status: OrganisationVerificationStatus | null): CandidateBadgeProps => {
    switch (status) {
      case OrganisationVerificationStatus.PENDING:
        return {
          variant: 'default',
          icon: 'clock',
          text: t('grants.organizationDetails.verificationStatus.pending')
        }
      case OrganisationVerificationStatus.VERIFIED:
        return {
          variant: 'positive',
          icon: 'check-badge',
          text: t('grants.organizationDetails.verificationStatus.verified')
        }
      case OrganisationVerificationStatus.REJECTED:
        return {
          variant: 'critical',
          icon: 'reject',
          text: t('grants.organizationDetails.verificationStatus.rejected')
        }
      default:
        return {
          variant: 'default',
          text: t('grants.organizationDetails.verificationStatus.unknown')
        }
    }
  }

  const badgeProps = candidateStateToBadgeMapping(verificationStatus)
  const activityTags = organisation.eligibilityResult?.confirmedActivitySubTags ?? []

  const valueOrNotAvailableString = (value?: string | null) => value || t('typography.notAvailable')

  return (
    <Flex direction="column" data-testid="candidate-card" className={styles.cardCtn}>
      <CardDashboard title={t('grants.organizationDetails.title')} data-testid="organization-details-card">
        <CardContent title={t('grants.organizationDetails.organization')} testId="organization-name">
          {organisation.name}
        </CardContent>
        <CardContent
          title={t('grants.organizationDetails.verificationStatus')}
          testId="organization-verification-status"
        >
          <Badge icon={badgeProps.icon} variant={badgeProps.variant}>
            {badgeProps.text}
          </Badge>
        </CardContent>
        <CardContent title={t('grants.organizationDetails.activity')} testId="organization-activity-tags">
          {activityTags.length > 0 ? (
            <Flex direction="row" gap={8} wrap="wrap" data-testid="activity-tags">
              {activityTags.map(activityTag => (
                <Badge key={activityTag.id} variant="default" icon="categories">
                  {activityTag.name}
                </Badge>
              ))}
            </Flex>
          ) : (
            t('typography.notAvailable')
          )}
        </CardContent>
        <CardContent title={t('grants.organizationDetails.website')} testId="organization-website">
          {organisation.website ? (
            <a href={organisation.website} target="_blank" rel="noopener noreferrer" className={styles.wrappedValue}>
              {organisation.website}
            </a>
          ) : (
            t('typography.notAvailable')
          )}
        </CardContent>
        <CardContent title={t('grants.organizationDetails.email')} testId="organization-email">
          {organisation.email ? (
            <a href={`mailto:${organisation.email}`} className={styles.wrappedValue}>
              {organisation.email}
            </a>
          ) : (
            t('typography.notAvailable')
          )}
        </CardContent>
        <CardContent title={t('grants.organizationDetails.country')} testId="organization-country">
          <Flex align="center" gap={8}>
            <FlagAvatar code3={organisation.country} />
            <span data-testid="organization-country-code">
              {getCountryNameByCode(organisation.country) || getCountryNameFromAlpha3Code(organisation.country)}
            </span>
          </Flex>
        </CardContent>
        <CardContent title={t('grants.organizationDetails.address')} testId="organization-address">
          <span className={styles.wrappedValue}>{valueOrNotAvailableString(organisation.address)}</span>
        </CardContent>
        <CardContent title={t('grants.organizationDetails.registryName')} testId="organization-registry-name">
          <span className={styles.wrappedValue}>{valueOrNotAvailableString(organisation.registryName)}</span>
        </CardContent>
        <CardContent title={t('grants.organizationDetails.registryId')} testId="organization-registry-id">
          <span className={styles.wrappedValue}>{valueOrNotAvailableString(organisation.registryId)}</span>
        </CardContent>
      </CardDashboard>
    </Flex>
  )
}
